import PageHero from "../components/PageHero";

const PILLARS = [
  {
    icon: "ri-pencil-ruler-2-line",
    title: "Modular Design & Engineering",
    desc: "Every villa is engineered as precision modules in our factory, so structural quality, thermal comfort and scenic placement are optimised before a single panel ships.",
  },
  {
    icon: "ri-leaf-line",
    title: "Sustainable Materials",
    desc: "We use engineered timber, steel frames and eco-insulation that cut embodied carbon and keep the villa light on its site — perfect for sensitive hill and forest locations.",
  },
  {
    icon: "ri-settings-3-line",
    title: "Factory Fabrication",
    desc: "Panels, glazing and interiors are fabricated off-site with tight tolerances and quality checks, shrinking build time to a fraction of traditional construction.",
  },
  {
    icon: "ri-hammer-line",
    title: "On-Site Assembly & Finishing",
    desc: "Modules are craned into place, then we complete fit-out, landscaping and resort-ready amenities so the villa is guest-ready from day one.",
  },
];

const STEPS = [
  {
    step: "01",
    icon: "ri-chat-3-line",
    title: "Design Consultation",
    desc: "Choose your villa model, location and intended use — staycation, Airbnb or boutique resort — and we tailor the plan to the site.",
  },
  {
    step: "02",
    icon: "ri-file-shield-line",
    title: "Site & Approvals",
    desc: "We handle land checks, local approvals and legal structuring so your project is compliant and bankable from the start.",
  },
  {
    step: "03",
    icon: "ri-tools-line",
    title: "Factory Fabrication",
    desc: "Your modules are precision-built off-site while foundations are prepared — most construction happens in weeks, not months.",
  },
  {
    step: "04",
    icon: "ri-truck-line",
    title: "Site Assembly",
    desc: "Modules arrive and are assembled on site in days, dramatically cutting weather risk, waste and construction delays.",
  },
  {
    step: "05",
    icon: "ri-sparkling-2-line",
    title: "Fit-Out & Landscaping",
    desc: "Interiors, utilities and outdoor decks are completed to a resort standard, ready for photos and guest bookings.",
  },
  {
    step: "06",
    icon: "ri-store-3-line",
    title: "Resort Launch",
    desc: "Your villa joins our managed hospitality network and starts earning — backed by marketing, housekeeping and bookings.",
  },
];

const MATERIALS = [
  { icon: "ri-tree-line", label: "Engineered timber" },
  { icon: "ri-building-2-line", label: "Precision steel frames" },
  { icon: "ri-focus-3-line", label: "Double-glazed glass" },
  { icon: "ri-sun-line", label: "Eco-insulation" },
  { icon: "ri-water-flash-line", label: "Rainwater harvesting" },
  { icon: "ri-battery-2-charge-line", label: "Solar-ready power" },
];

export default function AiTechnology() {
  return (
    <div className="w-full">
      <PageHero
        eyebrow="Design & Construction"
        title={
          <>
            From Blueprint to <span className="italic text-accent-300">Boutique Villa</span>
          </>
        }
        subtitle="A modular A-frame build that combines architect-grade design with factory precision — engineered for hospitality, built for the mountains, and ready to earn fast."
      />

      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-primary-600">
              <i className="ri-hammer-line"></i> How We Build
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-3">
              Four Pillars of Modular Construction
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {PILLARS.map((p) => (
              <div
                key={p.title}
                className="rounded-2xl bg-background-100 border border-background-200 p-6 hover:border-primary-300 hover:-translate-y-1 transition-all duration-300"
              >
                <span className="flex items-center justify-center w-12 h-12 rounded-2xl bg-primary-100 text-primary-700">
                  <i className={`${p.icon} text-2xl`}></i>
                </span>
                <h3 className="font-heading text-lg text-foreground-950 mt-5">{p.title}</h3>
                <p className="text-sm text-foreground-600 mt-3 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-12 md:py-16 bg-background-100">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-primary-600">
              <i className="ri-road-map-line"></i> The Journey
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-3">
              Six Steps to a Guest-Ready Villa
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {STEPS.map((s) => (
              <div
                key={s.step}
                className="relative rounded-2xl bg-background-50 border border-background-200 p-6 hover:border-primary-300 transition-colors"
              >
                <span className="absolute top-5 right-6 font-heading text-4xl font-bold text-primary-100">
                  {s.step}
                </span>
                <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-accent-100 text-accent-700">
                  <i className={`${s.icon} text-xl`}></i>
                </span>
                <h3 className="font-heading text-lg text-foreground-950 mt-4">{s.title}</h3>
                <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-primary-600">
            <i className="ri-leaf-line"></i> Materials
          </span>
          <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-3">
            Built to Last, Kind to the Site
          </h2>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
            {MATERIALS.map((d) => (
              <div
                key={d.label}
                className="flex items-center gap-3 rounded-xl bg-background-100 border border-background-200 px-4 py-3.5 text-left"
              >
                <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-100 text-primary-700 shrink-0">
                  <i className={`${d.icon} text-lg`}></i>
                </span>
                <span className="text-sm font-medium text-foreground-800">{d.label}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-foreground-600 mt-8 max-w-2xl mx-auto leading-relaxed">
            The result: a beautiful, durable and sustainable villa that guests love — and a
            <span className="font-semibold text-foreground-800"> faster path to first income</span> than traditional builds.
          </p>
          <a
            href="/listings"
            className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-3 mt-8 hover:bg-primary-700 transition-colors"
          >
            Explore Villa Models <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>
    </div>
  );
}